import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Ticket, 
  UserCircle,
  LogOut,
  Menu,
  Settings,
  Building2,
  Megaphone,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import { Button } from './ui/button';

export const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin', 'manager', 'sales_executive', 'service_executive'] },
    { icon: Users, label: 'Customers', path: '/customers', roles: ['admin', 'manager', 'sales_executive', 'service_executive'] },
    { icon: FileText, label: 'Enquiries', path: '/enquiries', roles: ['admin', 'manager', 'sales_executive'] },
    { icon: Ticket, label: 'Tickets', path: '/tickets', roles: ['admin', 'manager', 'sales_executive', 'service_executive'] },
    { icon: Megaphone, label: 'Marketing Leads', path: '/marketing-leads', roles: ['admin', 'manager', 'sales_executive', 'service_executive'] },
    { icon: BarChart3, label: 'Marketing Reports', path: '/marketing-reports', roles: ['admin', 'manager'] },
    { icon: TrendingUp, label: 'Branch Performance', path: '/branch-performance', roles: ['admin'] },
    { icon: UserCircle, label: 'Team Members', path: '/users', roles: ['admin'] },
    { icon: Building2, label: 'Branches', path: '/branches', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  return (
    <>
      {/* Mobile Overlay */}
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 z-40 h-screen w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_b20a2d16-04dd-4f4d-a217-a3cf1fe24eb4/artifacts/60nwz541_Konattu%20CRM%20Logo12.png" 
                alt="Konattu CRM" 
                className="h-10 w-auto object-contain"
                data-testid="sidebar-logo"
              />
            </div>
            <button 
              onClick={() => setOpen(false)}
              className="md:hidden"
              data-testid="close-sidebar-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200
                    ${isActive 
                      ? 'bg-primary text-white' 
                      : 'text-slate-700 hover:bg-slate-100'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t border-slate-200 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="space-y-1">
              <Button
                onClick={() => navigate('/profile')}
                variant="ghost"
                className="w-full justify-start gap-2"
                data-testid="profile-button"
              >
                <Settings className="h-4 w-4" />
                <span>Profile Settings</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};